import { capitalize } from '@angle/utils'
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import {
  CenteredSpinner,
  ErrorFallback,
  Modal,
  Overlay,
  SummaryOfBenefitsCoverage,
  useErrorHandler,
  useToaster,
  withErrorBoundary
} from 'react-angle-dashboard-components'
import { useQuery } from 'react-query'
import { useHistory } from 'react-router-dom'
import AgeSlider from '../../components/AgeSlider'
import { InfoIcon, InfoP, InfoWrapper } from '../../components/Info'
import PlanList from '../../components/PlanList'
import useStep from '../../hooks/use-step'
import { ISelectablePlan } from '../../interfaces/plan'
import { Paths } from '../../routes/paths'
import { groupService, planService } from '../../services'
import { GbsStore } from '../../store'
import { Actions, SetStepData } from '../../store/actions'
import {
  GbsStep,
  InitialQuoteInformation,
  RecommendedPlansData
} from '../../store/interfaces'
import { useInitialQuoteInformation, useStepData } from '../../store/selectors'
import {
  Action,
  Highlight,
  RecommendedPlansWrapper,
  Subtitle,
  Title
} from './styles'

const stepKey = 'recommendedPlans'

export const RecommendedPlans = () => {
  const overlayRef = useRef(null)
  const [show, setShow] = useState(false)
  const [currentSBCData, setSBCData] = useState<any>(null)
  const [sbcIsLoading, setSbcLoading] = useState(false)
  const [capturingIsLoading, setCapturingLoading] = useState(false)
  const { show: showToast } = useToaster()

  const { dispatch } = useContext(GbsStore)
  const history = useHistory()

  const contribution = useStepData('contribution')

  const recommendedPlansData =
    useStepData<RecommendedPlansData>('recommendedPlans')

  const avgAge = recommendedPlansData?.avgAge
  const contributionValue = contribution?.value
  const dependentValue = contribution?.dependentContribution

  const initialQuoteData: InitialQuoteInformation | null =
    useInitialQuoteInformation()

  const { data, isLoading, error } = useQuery(
    ['plans', avgAge, contributionValue, dependentValue],
    () =>
      planService.getSelectablePlans({
        avg_age: avgAge
      }),
    {
      cacheTime: 30000,
      refetchInterval: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      refetchIntervalInBackground: false
    }
  )

  useErrorHandler(error)

  const [plans, setPlans] = useState<ISelectablePlan[]>([])

  useEffect(() => {
    if (data) {
      setPlans(data)
      const selectedPlans = data.filter((plan) => plan.selected)
      const action: SetStepData<Partial<RecommendedPlansData>> = {
        type: Actions.SetStepData,
        payload: {
          stepKey,
          stepData: {
            selectedPlans: selectedPlans.length > 0 ? selectedPlans : null
          }
        }
      }

      dispatch(action)
    }
  }, [data, dispatch])

  const [showAvgAgeModal, setShowAvgAgeModal] = useState(false)

  const handleNewAvgAge = (newAvgAge: any) => {
    setShowAvgAgeModal(false)
    const action: SetStepData<Partial<RecommendedPlansData>> = {
      type: Actions.SetStepData,
      payload: {
        stepKey,
        stepData: {
          ...recommendedPlansData,
          avgAge: newAvgAge
        }
      }
    }

    dispatch(action)
    //setAverageAge(newAvgAge)
  }

  const stepConfig: Omit<GbsStep, 'data'> = useMemo(() => {
    const selectedPlans = plans.filter((plan) => plan.selected)

    const capture = async () => {
      try {
        setCapturingLoading(true)
        await groupService.modify({
          plans: selectedPlans.map((m) => ({
            plan_id: m.id,
            selected: m.selected
          })) as any
        })

        setCapturingLoading(false)
        dispatch({
          type: Actions.SetStepData,
          payload: {
            stepKey,
            stepData: { plans }
          }
        })
      } catch (e: any) {
        setCapturingLoading(false)
        showToast({
          message: e.message || 'Something went wrong',
          icon: 'icon-cancel',
          type: 'danger'
        })
        return false
      }
    }

    return {
      bottomBar: {
        buttons: [
          {
            text: 'Next',
            variant: 'primary',
            disabled: selectedPlans.length <= 0 || capturingIsLoading,
            callback: async () => {
              capture()
              history.push(Paths.employees)
            }
          }
        ]
      },
      topBar: {
        completedPercentage: 60,
        showEdit: true,
        backCallback: () => history.push(Paths.avgAge)
      }
    }
  }, [
    history,
    plans,
    dispatch,
    showToast,
    capturingIsLoading,
    setCapturingLoading
  ])

  useStep(stepKey, stepConfig, dispatch)

  const handlePlanSelect = (selected: boolean, id: string) => {
    const newPlans = [...plans]
    const modifiedPlanIndex = newPlans.findIndex((plan) => plan.id === id)
    newPlans[modifiedPlanIndex].selected = selected

    setPlans(newPlans)

    const selectedPlans = plans.filter((plan) => plan.selected)

    const action: SetStepData<Partial<RecommendedPlansData>> = {
      type: Actions.SetStepData,
      payload: {
        stepKey,
        stepData: {
          selectedPlans: selectedPlans.length > 0 ? selectedPlans : null
        }
      }
    }

    dispatch(action)
  }

  const handleShowPlanDetails = async (id: string, plan: ISelectablePlan) => {
    try {
      setSbcLoading(true)
      setShow(true)
      const { events, faqs } = await planService.getSBC(id)

      setSBCData({
        benefits: events,
        faqs,
        plan,
        coverageInfo: {
          plan: {
            name: plan.name,
            metalTier: plan?.tier,
            type: plan?.type
          },
          coveragePeriod: {
            from: plan?.startDate,
            to: plan?.endDate
          },
          coverageFor: plan?.eligibleMember?.map(capitalize).join('/') //'Single/Family'
        }
      })
      setSbcLoading(false)
    } catch (e) {
      setSbcLoading(false)
      setShow(false)
      showToast({
        message: "We can't get the SBC information",
        icon: 'icon-cancel',
        type: 'danger'
      })
    }
  }

  return (
    <>
      <RecommendedPlansWrapper>
        <Title>Our Recommended Plans</Title>
        <Subtitle>Select plans to continue to the next step</Subtitle>
        <InfoWrapper>
          <InfoIcon>!</InfoIcon>
          <InfoP>
            The recommended plans below reflect an average employee age of{' '}
            <Highlight>{recommendedPlansData?.avgAge} years old</Highlight> for{' '}
            <Highlight>
              {initialQuoteData?.numberOfEmployees} employees
            </Highlight>{' '}
            in zipcode <Highlight>{initialQuoteData?.zip}</Highlight>.
          </InfoP>
          <InfoP>
            <Action
              onClick={() => setShowAvgAgeModal(!showAvgAgeModal)}
              ref={overlayRef}
            >
              Click here
            </Action>{' '}
            to adjust the average for more accurate pricing.
            <Overlay
              target={overlayRef.current}
              show={showAvgAgeModal}
              placement="bottom"
            >
              {({ placement, arrowProps, show: _show, popper, ...props }) => (
                <AgeSlider
                  {...props}
                  value={recommendedPlansData?.avgAge || 0}
                  setValue={() => {}}
                  style={{ ...props.style }}
                  continueCallback={handleNewAvgAge}
                />
              )}
            </Overlay>
          </InfoP>
        </InfoWrapper>

        {isLoading ? (
          <CenteredSpinner />
        ) : (
          <PlanList
            plans={plans}
            onPlanSelect={handlePlanSelect}
            onShowPlanDetails={handleShowPlanDetails}
          />
        )}
        {/* <OtherCarrier /> */}
      </RecommendedPlansWrapper>
      <Modal
        show={show}
        onHide={() => setShow(false)}
        dialogClassName="w-100 m-0 mw-100"
        className="px-0"
        aria-labelledby="example-custom-modal-styling-title"
        style={{ zIndex: 9999 }}
      >
        <Modal.Header closeButton></Modal.Header>
        <Modal.Body style={{ minHeight: '95vh' }}>
          {sbcIsLoading && <CenteredSpinner />}
          {currentSBCData && !sbcIsLoading && (
            <SummaryOfBenefitsCoverage
              coverageInfo={currentSBCData.coverageInfo}
              benefits={currentSBCData.benefits}
              faqs={currentSBCData.faqs}
            />
          )}
        </Modal.Body>
      </Modal>
    </>
  )
}

export default withErrorBoundary(RecommendedPlans, {
  FallbackComponent: () => (
    <RecommendedPlansWrapper>
      <ErrorFallback />
    </RecommendedPlansWrapper>
  )
})
